import { cloneDeep, isObject, isFunction } from 'lodash-es';
import { Publisher } from './publisher.js';
// Exported for reuse purposes in for instance eva-sdk-signalr
//
export { Publisher } from './publisher.js';
/**
 * The settings manager wraps a collection of settings and provides
 * methods to register and retrieve groups of settings
 * It also helps track changes to the settings
 *
 * @export
 * @class SettingsManager
 */
export class SettingsManager extends Publisher {
    /**
     * The actual settings data
     */
    settings = {};
    /**
     * Register a new settings group
     *
     * @param {string} groupName The name of the group to register
     * @param {*} [defaultSetting={}] The default settings for the group
     */
    register(groupName, defaultSetting = {}) {
        if (this.settings[groupName]) {
            throw new Error(`SETTINGS: Duplicate registration for ${groupName}`);
        }
        // Make a copy of the default settings
        //
        this.settings[groupName] = cloneDeep(defaultSetting);
    }
    /**
     * Retrieves a setting group
     *
     * @param {string} groupName The name of the group to fetch
     */
    getGroup(groupName) {
        return cloneDeep(this.settings[groupName]);
    }
    /**
     * Retrieves a specific setting
     *
     * @param {string} groupName
     * @param {*} setting
     */
    get(groupName, settingName) {
        const group = this.getGroup(groupName) || {};
        const settingValue = group[settingName];
        if (isObject(settingValue) && !isFunction(settingValue)) {
            return cloneDeep(settingValue);
        }
        return settingValue;
    }
    /**
     * Updates a specific setting and returns the change details
     *
     * @param {string} groupName
     * @param {*} setting
     */
    set(groupName, settingName, setting) {
        const oldValue = cloneDeep(this.get(groupName, settingName));
        this.settings[groupName][settingName] = setting;
        const change = {
            group: groupName,
            name: settingName,
            old: oldValue,
            new: setting,
        };
        // Always publishing all changes. Used to have change detection here but this proved
        // fickle with object type settings
        //
        this.publish({ channel: `eva.setting.${change.group}.${change.name}`, value: change });
        return change;
    }
}
export const settings = new SettingsManager();
// This is more of an SDK family version.
// Because this new SDK is modular it makes no sense to emit a single package
// version here
//
export const SDK_CLIENT_VERSION = '2.0.0';
// Register the default settings
//
export const SETTINGS_GROUP_NAME = 'evaCore';
// Setup the core settings
//
const defaultSettings = {
    language: 'en-EN',
    clientName: 'eva-sdk-core',
    clientVersion: SDK_CLIENT_VERSION,
    appName: 'UNKNOWN',
    appVersion: '0.0.0',
};
settings.register(SETTINGS_GROUP_NAME, defaultSettings);
/**
 * Retrieves the core SDK setting
 *
 * @export
 * @returns {IEvaCoreSettings}
 */
export function getCoreSettings() {
    return settings.getGroup(SETTINGS_GROUP_NAME);
}
/**
 * Changes a setting for the core SDK settings
 *
 * @export
 * @param {string} settingName
 * @param {*} setting
 * @returns {ISettingsChange}
 */
export function setCoreSetting(settingName, setting) {
    return settings.set(SETTINGS_GROUP_NAME, settingName, setting);
}
//# sourceMappingURL=eva-sdk-core-settings.js.map